import { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";

import { api } from "../../api.js";
import {
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

type XeroContactOption = {
  contactId: string;
  name: string;
  emailAddress: string | null;
};

interface LinkDoctorToXeroDialogProps {
  open: boolean;
  doctorUserId: string;
  doctorName: string;
  doctorEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function LinkDoctorToXeroDialog({
  open,
  doctorUserId,
  doctorName,
  doctorEmail,
  onClose,
  onSuccess,
}: LinkDoctorToXeroDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState<XeroContactOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] =
    useState<XeroContactOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [currentContactId, setCurrentContactId] = useState<string | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(false);

  // Load the current linked contact ID when the dialog opens
  useEffect(() => {
    if (!open) {
      setSelectedContact(null);
      setSearchQuery("");
      setOptions([]);
      setCurrentContactId(null);
      return;
    }

    let cancelled = false;
    setLoadingCurrent(true);

    const loadInitialData = async () => {
      try {
        const response =
          await api.configuration.getUserXeroContactId(doctorUserId);
        if (!cancelled) {
          setCurrentContactId(response.data?.xeroContactId ?? null);
        }
      } catch {
        /* ignore — field may not exist yet */
      }

      // Auto-search by email to show if there's already a matching contact
      if (doctorEmail && !cancelled) {
        try {
          const searchResponse =
            await api.configuration.searchXeroContacts(doctorEmail);
          if (!cancelled) {
            setOptions(searchResponse.data);
          }
        } catch {
          /* ignore — Xero may not be connected */
        }
      }

      if (!cancelled) setLoadingCurrent(false);
    };

    void loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [open, doctorUserId, doctorEmail]);

  const searchContacts = useCallback((query: string) => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    api.configuration
      .searchXeroContacts(query)
      .then((response) => {
        setOptions(response.data);
      })
      .catch(() => {
        setOptions([]);
        renderErrorToast({
          message: "Failed to search Xero contacts. Check Xero is connected.",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Debounce search
  useEffect(() => {
    if (searchQuery.length < 2) return;

    const timer = setTimeout(() => {
      searchContacts(searchQuery);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery, searchContacts]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.configuration.setUserXeroContactId(
        doctorUserId,
        selectedContact?.contactId ?? null,
      );
      renderSuccessToast({
        message: selectedContact
          ? `Linked ${doctorName} to Xero contact "${selectedContact.name}"`
          : `Unlinked ${doctorName} from Xero`,
      });
      onSuccess();
    } catch {
      renderErrorToast({ message: "Failed to update Xero contact link" });
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    setSaving(true);
    try {
      await api.configuration.setUserXeroContactId(doctorUserId, null);
      renderSuccessToast({ message: `Unlinked ${doctorName} from Xero` });
      onSuccess();
    } catch {
      renderErrorToast({ message: "Failed to unlink Xero contact" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Doctor to Xero Contact</DialogTitle>
      <DialogContent>
        {loadingCurrent ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Search for an existing Xero contact to link to{" "}
              <strong>{doctorName}</strong>.
            </Typography>

            {currentContactId && (
              <Typography variant="body2" sx={{ mb: 2, color: "success.main" }}>
                Currently linked to Xero Contact ID:{" "}
                <code>{currentContactId}</code>
              </Typography>
            )}

            <Autocomplete
              options={options}
              getOptionLabel={(option) =>
                option.emailAddress
                  ? `${option.name} (${option.emailAddress})`
                  : option.name
              }
              value={selectedContact}
              onChange={(_event, newValue) => {
                setSelectedContact(newValue);
              }}
              onInputChange={(_event, newInputValue, reason) => {
                if (reason === "input") {
                  setSearchQuery(newInputValue);
                }
              }}
              loading={loading}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, val) =>
                option.contactId === val.contactId
              }
              noOptionsText={
                searchQuery.length < 2
                  ? "Type at least 2 characters to search"
                  : "No Xero contacts found"
              }
              renderOption={(props, option) => (
                <li {...props} key={option.contactId}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.emailAddress && (
                      <Typography variant="caption" color="text.secondary">
                        {option.emailAddress}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="Search Xero contacts"
                  placeholder="Search by name or email"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {currentContactId && (
          <Button
            onClick={() => void handleUnlink()}
            disabled={saving}
            color="error"
          >
            Unlink
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSave()}
          variant="contained"
          disabled={!selectedContact || saving}
        >
          {saving ? "Saving\u2026" : "Link Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
